import type { AppliedConfiguration, SwadeModConfigurationInterface } from '@/types/items';

// A transfer's identity includes what it carries (spec 2026-09-10 carried modifications §4): the
// entries' configurations minus their labels, ignoring the editor's transient slot keys. Kept local
// rather than imported from container-carry, which imports this module.
const carryIdentity = (configuration: SwadeModConfigurationInterface): string => {
  const carry = (configuration as any).carry;
  if (!Array.isArray(carry)) return '[]';
  return JSON.stringify(carry.map((entry: any) => {
    const c = entry?.configuration;
    if (!c || typeof c !== 'object') return null;
    const { label: _l, summary: _s, ...rest } = c;
    return Object.fromEntries(Object.entries(rest).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
  }));
};

/**
 * Field-by-field comparison of two modification configurations for identity purposes.
 *
 * Intentionally skips `label` — labels can change between GM edits without changing
 * the semantic choice (the underlying path/element/value). For inner choices in a
 * Choice Set, `element` is always a scalar (string or number), so `===` is correct
 * for the eight permitted inner paths. …and, for a container modification, what it carries.
 */
export function matchesConfiguration(
  a: SwadeModConfigurationInterface | undefined,
  b: SwadeModConfigurationInterface | undefined,
): boolean {
  if (!a || !b) return false;
  return a.path === b.path
      && a.element === b.element
      && a.operator === b.operator
      && String(a.value ?? '') === String(b.value ?? '')
      && a.valueType === b.valueType
      && carryIdentity(a) === carryIdentity(b);
}

/**
 * The modification a record resolves. `sourceModUuid` is canonical; `choiceSetUuid` is the
 * deprecated legacy alias (items.ts) that choice-set records still populate. Every reader goes
 * through here — never compare either field raw.
 */
export const resolvedModificationUuid = (r: AppliedConfiguration | undefined): string | undefined =>
  r?.sourceModUuid ?? r?.choiceSetUuid;

/**
 * Every record a given source container holds for one modification — a SET, because a container
 * modification may be applied to several targets. An undefined or empty modUuid matches nothing:
 * a record with no identity must never be treated as "the same override" as another.
 */
export function recordsForSourceModification(
  records: AppliedConfiguration[] | undefined,
  sourceUuid: string | null | undefined,
  modUuid: string | undefined,
): AppliedConfiguration[] {
  if (!sourceUuid || !modUuid) return [];
  return (records ?? []).filter((r) => r.uuid === sourceUuid && resolvedModificationUuid(r) === modUuid);
}

/** Order-independent multiset equality by matchesConfiguration. */
export function sameConfigurationSet(
  a: SwadeModConfigurationInterface[],
  b: SwadeModConfigurationInterface[],
): boolean {
  if (a.length !== b.length) return false;
  const remaining = [...b];
  for (const x of a) {
    const i = remaining.findIndex((y) => matchesConfiguration(x, y));
    if (i < 0) return false;
    remaining.splice(i, 1);
  }
  return true;
}

/**
 * previous -> next as three disjoint lists. Comparison is by matchesConfiguration, so a reorder is
 * no change and a changed value is a remove+add.
 */
export function diffConfigurationSets(
  previous: SwadeModConfigurationInterface[],
  next: SwadeModConfigurationInterface[],
): { unchanged: SwadeModConfigurationInterface[]; removed: SwadeModConfigurationInterface[]; added: SwadeModConfigurationInterface[] } {
  const unchanged: SwadeModConfigurationInterface[] = [];
  const removed: SwadeModConfigurationInterface[] = [];
  const remainingNext = [...next];
  for (const p of previous) {
    const i = remainingNext.findIndex((n) => matchesConfiguration(p, n));
    if (i < 0) removed.push(p);
    else { unchanged.push(p); remainingNext.splice(i, 1); }
  }
  return { unchanged, removed, added: remainingNext };
}
